import L from "leaflet";

/** Bounding-box size in degrees² of a country's largest polygon; decides at which zoom its name appears. */
const MIN_AREA_FOR_ZOOM = (zoom) => (zoom <= 2 ? 250 : zoom === 3 ? 30 : zoom === 4 ? 4 : 0);

const escapeHtml = (value) =>
  String(value ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);

/** Centre of the largest polygon, so islands and overseas parts don't pull the label off the mainland. */
const labelAnchor = (geometry) => {
  const polygons =
    geometry?.type === "Polygon" ? [geometry.coordinates] : geometry?.type === "MultiPolygon" ? geometry.coordinates : [];
  let best = null;
  polygons.forEach((polygon) => {
    const ring = polygon?.[0];
    if (!ring?.length) return;
    const bounds = L.latLngBounds(ring.map(([lng, lat]) => [lat, lng]));
    const area = Math.abs((bounds.getNorth() - bounds.getSouth()) * (bounds.getEast() - bounds.getWest()));
    if (!best || area > best.area) best = { center: bounds.getCenter(), area };
  });
  return best;
};

/**
 * Country names drawn from the world GeoJSON. Label tiles have almost no text at zoom 2–3,
 * so the dark world maps place their own names instead.
 * Returns a handle with remove().
 */
export const addCountryLabels = (map, geojson, { pane, color = "#c7d2e3" } = {}) => {
  const group = L.layerGroup();
  const items = [];
  (geojson?.features || []).forEach((feature) => {
    const name = feature?.properties?.name;
    if (!name || name === "Antarctica") return;
    const anchor = labelAnchor(feature.geometry);
    if (!anchor) return;
    const icon = L.divIcon({
      className: "",
      iconSize: null,
      html: `<span style="position:absolute;transform:translate(-50%,-50%);white-space:nowrap;font:600 10px/1 system-ui,sans-serif;letter-spacing:0.02em;color:${color};text-shadow:0 1px 2px rgba(0,0,0,0.7);pointer-events:none">${escapeHtml(name)}</span>`,
    });
    items.push({ area: anchor.area, marker: L.marker(anchor.center, { icon, interactive: false, keyboard: false, pane }) });
  });

  const update = () => {
    const min = MIN_AREA_FOR_ZOOM(map.getZoom());
    items.forEach(({ area, marker }) => {
      const show = area >= min;
      if (show && !group.hasLayer(marker)) group.addLayer(marker);
      else if (!show && group.hasLayer(marker)) group.removeLayer(marker);
    });
  };

  update();
  group.addTo(map);
  map.on("zoomend", update);
  return {
    remove: () => {
      map.off("zoomend", update);
      group.remove();
    },
  };
};
